import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Calendar,
  Heart,
  Lightbulb,
  Scale,
  HelpCircle,
  ArrowRight,
} from "lucide-react";
import {
  listLegalStories,
  getTodayLegalStory,
} from "@/lib/api/daily-legal.functions";
import { StorySocial } from "@/components/site/StorySocial";

const CANONICAL = "https://nkadimengdannylcs.com/daily-legal";
const DEFAULT_OG = "https://nkadimengdannylcs.com/og/news-default.jpg";

function hashString(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
}

function bust(url: string, seed: string): string {
  const v = hashString(seed).slice(0, 8);
  return url.includes("?") ? `${url}&v=${v}` : `${url}?v=${v}`;
}

function clamp(text: string, max: number): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  return (stop > max * 0.5 ? cut.slice(0, stop + 1) : cut.replace(/\s+\S*$/, "")) + "…";
}

export const Route = createFileRoute("/daily-legal")({
  loader: async () => {
    const data = await getTodayLegalStory().catch(() => ({ story: null }));
    return { today: (data as any)?.story ?? null };
  },
  head: ({ loaderData }) => {
    const story: any = (loaderData as any)?.today;
    const title = story?.question
      ? `${clamp(story.question, 90)} — Latest Legal Story`
      : "Latest Legal Story | Nkadimeng Danny Legal & Compliance Support";
    const description = story
      ? clamp(
          `${story.story} Brief solution: ${story.solution}`,
          290,
        )
      : "A new South African legal question, heartfelt story and brief solution — refreshed every hour. Real situations, real Acts, real answers.";
    // Cache-bust the OG image whenever the featured story (or its category)
    // changes, so Facebook and Twitter re-scrape and don't serve a stale
    // preview from cache.
    const seed = story
      ? `${story.id}|${story.category ?? ""}|${story.publish_at ?? story.publish_date ?? ""}`
      : "daily-legal-default";
    const image = bust(DEFAULT_OG, seed);

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: CANONICAL },
        { property: "og:image", content: image },
        { property: "og:image:secure_url", content: image },
        { property: "og:image:alt", content: story?.question ?? "Latest legal story" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: story?.question ?? "Latest legal story" },
      ],
      links: [{ rel: "canonical", href: CANONICAL }],
    };
  },
  component: DailyLegalPage,
});

function DailyLegalPage() {
  const fetchStories = useServerFn(listLegalStories);
  const { data, isLoading } = useQuery({
    queryKey: ["daily-legal-stories"],
    queryFn: () => fetchStories(),
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
  });

  const stories = data?.stories ?? [];
  const today = stories[0];
  const archive = stories.slice(1);

  return (
    <div className="bg-background">
      <section className="bg-gradient-to-b from-gold/10 to-transparent py-14">
        <div className="container-narrow">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Published hourly
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            Latest Legal Story
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            A real-life South African legal scenario every hour — the question,
            the human story behind it, and a brief practical solution citing
            the Act that matters.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="container-narrow space-y-10">
          {isLoading && (
            <p className="text-center text-muted-foreground">Loading…</p>
          )}

          {!isLoading && !today && (
            <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
              <p className="text-muted-foreground">
                Today's story is being prepared. Please check back shortly.
              </p>
            </div>
          )}

          {today && <StoryCard story={today} featured />}

          {archive.length > 0 && (
            <div>
              <h2 className="mb-5 font-display text-2xl text-navy">
                Previous stories
              </h2>
              <div className="grid gap-5 md:grid-cols-2">
                {archive.map((s) => (
                  <StoryCard key={s.id} story={s} />
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-border bg-navy/5 p-6 text-center">
            <Scale className="mx-auto h-7 w-7 text-gold" />
            <h3 className="mt-2 font-display text-xl text-navy">
              Facing a similar situation?
            </h3>
            <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">
              Book a consultation and we'll apply the Act to your specific
              facts.
            </p>
            <Link
              to="/book"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-medium text-navy-foreground hover:bg-navy/90"
            >
              Book a consultation <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function StoryCard({
  story,
  featured = false,
}: {
  story: {
    id: string;
    publish_date: string;
    publish_at?: string;
    question: string;
    story: string;
    solution: string;
    category: string;
    legislation_refs: string[];
  };
  featured?: boolean;
}) {
  const published = new Date(story.publish_at ?? story.publish_date);
  const date = published.toLocaleString("en-ZA", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <article
      className={
        featured
          ? "rounded-2xl border-2 border-gold/40 bg-card p-7 shadow-soft"
          : "rounded-xl border border-border bg-card p-6"
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Calendar className="h-3.5 w-3.5" /> {date}
        </span>
        <span className="rounded-full bg-navy/10 px-2 py-0.5 font-medium text-navy">
          {story.category}
        </span>
      </div>

      <div className="mt-4">
        <div className="flex items-start gap-2">
          <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
          <h3
            className={
              featured
                ? "font-display text-xl text-navy"
                : "font-display text-lg text-navy"
            }
          >
            {story.question}
          </h3>
        </div>
      </div>

      <div className="mt-5">
        <p className="mb-1.5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Heart className="h-3.5 w-3.5 text-rose-500" /> The story
        </p>
        <p className="text-sm leading-relaxed text-foreground/90">
          {story.story}
        </p>
      </div>

      <div className="mt-5 rounded-lg border border-gold/30 bg-gold/5 p-4">
        <p className="mb-1.5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-navy">
          <Lightbulb className="h-3.5 w-3.5" /> Brief solution
        </p>
        <p className="text-sm leading-relaxed text-foreground">
          {story.solution}
        </p>
        {story.legislation_refs?.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
            {story.legislation_refs.map((r) => (
              <li key={r} className="flex items-start gap-1.5">
                <Scale className="mt-0.5 h-3 w-3 shrink-0 text-gold" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {featured && (
        <StorySocial
          storyId={story.id}
          shareText={`${story.question}\n\n${story.story}\n\nBrief solution: ${story.solution}`}
          shareUrl={
            typeof window !== "undefined"
              ? `${window.location.origin}/daily-legal`
              : "/daily-legal"
          }
        />
      )}
    </article>
  );
}
